// import { preCalculatedResultForDevMode } from "../pre-calculated-result";
import { Cmd } from "@typescript-tea/core";
import { PropertyValueSet } from "@genesys/property";
import { getCoordinateFromClimateDataProperties } from "../../tools";
import { State, BaseState, Action } from "../";
import { getInitialInputs } from "./initial-inputs";
import {
  createLoadDefinitions,
  extractLoadProperties,
  extractTopLevelOverrides,
  extractUserSettings,
  getHighestMoistureLoadClaim,
  getPropertyDefinitions
} from "./tools";
import * as SharedState from "../../../shared-state";
import * as PropertiesSelector from "../../../properties-selector";
import * as GraphQLTypes from "../../../graphql-types";
import * as ClimateSettingsEditor from "../../components/climate-settings-editor";
import * as Main from "../../main-content";
import * as PrintParams from "../../../print-params";

const moistureLoadSupportedLanguaghges = ["en-US", "de-DE", "sv-SE"];

export function buildInitialState(
  productQuery: GraphQLTypes.MoistureLoadCalculationProductQuery,
  userQuery: GraphQLTypes.MoistureLoadCalculationUserQuery,
  sharedState: SharedState.State,
  state: State
): readonly [
  State,
  Cmd<Action>?,
  ReadonlyArray<SharedState.Action | undefined>?
] {
  const moistureLoadItem = userQuery.user.moistureLoadByMoistureLoadNo;

  if (!moistureLoadItem) {
    return [
      { ...state, errorMessage: "Moisture load not found. Or Unauthorized" }
    ];
  }

  const moistureLoadRules = productQuery.product.moistureLoadRules;

  // Extract property definitions
  const propertyDefinitions = getPropertyDefinitions(
    productQuery.product.moistureLoadRules,
    sharedState.translate
  );

  const propertiesToKeep = propertyDefinitions.map(p => p.name);
  const binLocations = productQuery.product.binDataLocations;

  // Extract user settings
  const { moistureLoadUserSettings, climateDataFromMlSettings } =
    extractUserSettings(userQuery);

  // Extract claims
  const moistureLoadClaimValue = getHighestMoistureLoadClaim(sharedState);

  // Create load definitions
  const allLoads = createLoadDefinitions(moistureLoadClaimValue);

  // Get all load-related properties
  const { allLoadProperties, allOverridableLoadProperties } =
    extractLoadProperties(allLoads);

  // Get top-level property overrides
  const { topLevelProperties, topLevelOverridableProperties } =
    extractTopLevelOverrides(
      propertiesToKeep,
      allLoadProperties,
      allOverridableLoadProperties
    );

  const initialSelections = getInitialInputs(
    moistureLoadItem as any,
    propertiesToKeep,
    productQuery,
    sharedState,
    propertyDefinitions,
    climateDataFromMlSettings,
    PropertyValueSet.fromString(
      moistureLoadUserSettings?.moistureLoadSettings || ""
    ),
    allLoads,
    { topLevelProperties, topLevelOverridableProperties }
  );

  const { binData, climateData, moistureLoadSettings, mainSettingsOverrides } =
    initialSelections;

  const coordinate = getCoordinateFromClimateDataProperties(climateData);

  const baseState: BaseState = {
    moistureLoadNo: moistureLoadItem.moistureloadFile.moistureLoadNo,
    revisionNo: moistureLoadItem.revisionNo,
    defaultSettings: PropertyValueSet.fromString(
      moistureLoadRules.defaultSettings
    ),
    propertyDefinitions,
    moistureLoadClaimValue,
    moistureLoadUserSettings,
    initialSelections
  };

  const languages = productQuery.product.languages.filter(x =>
    moistureLoadSupportedLanguaghges.includes(x.id)
  );
  const calcResult = moistureLoadItem.moistureLoadResult
    ? {
        success: true,
        moistureLoadSettingsErrors: [],
        result: moistureLoadItem.moistureLoadResult
      }
    : undefined;

  const currentState: State = {
    ...state,
    properties: {
      topLevelProperties,
      topLevelOverrideProperties: topLevelOverridableProperties,
      allLoadProperites: allLoadProperties,
      allOverridableLoadProperties: allOverridableLoadProperties
    },
    moistureLoadId: moistureLoadItem.id,
    moistureLoadFileId: moistureLoadItem.moistureloadFile.id,
    languages
  };

  const moistureLoadNo = sharedState.genesysPrefix.moistureLoadNo(
    moistureLoadItem.moistureloadFile.moistureLoadNo,
    moistureLoadItem.revisionNo
  );

  const fileName = moistureLoadItem.moistureloadFile.name;

  const browserTitle = `${moistureLoadNo} ${fileName}`;

  const [climateSettingsEditorState, climatSettingsEditorCmd] =
    ClimateSettingsEditor.init(
      baseState,
      sharedState,
      climateData,
      binLocations,
      coordinate,
      binData
    );

  const [mainstate] = Main.init(
    baseState,
    initialSelections.systemOperationTime,
    initialSelections.loadSettings
  );

  const propertiesSelectorState = PropertiesSelector.init(moistureLoadSettings);

  return [
    {
      ...currentState,
      ...baseState,
      calcResult: calcResult,
      developerSettings: {
        ...currentState.developerSettings,
        showDevResults: !!calcResult?.result?.allBinResults?.length
      },
      printParamsState: PrintParams.init(languages, {
        selectedPaperSize: PrintParams.paperSizes[0].size,
        selectedHeaderLabel: PrintParams.headerLabels[0],
        selectedLanguage: "en-US"
      })[0],

      mainContentState: mainstate,
      topLevelCaseOverrides: mainSettingsOverrides,
      propertiesSelectorState,
      climateSettingsEditorState,
      productQueryMoistureLoad: productQuery,
      userQuery: userQuery
    },
    Cmd.map(Action.dispatchClimateSettingsEditor, climatSettingsEditorCmd),
    [SharedState.Action.setBrowserTitle(browserTitle)]
  ];
}
